import React, { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { ModuleAccessGuard } from '@/components/ModuleAccessGuard';
import { DataTable } from '@/components/shared/DataTable';
import { Button } from '@/components/ui/button';
import { useParserJobs, useBulkJobAction } from '@/hooks/useParserJobs';
import { useToast } from '@/hooks/use-toast';
import { Eye, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const DevParserQueue = () => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const { toast } = useToast();
  const filters = {
    progress_status: 'failed',
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
  };

  const { data, isLoading } = useParserJobs(filters);
  const bulkJobAction = useBulkJobAction();

  const columns = [
    {
      key: 'job_id',
      header: 'Job ID',
      render: (item: any) => (
        <span className="font-mono text-sm">{item.id.slice(0, 8)}</span>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (item: any) => item.customer_name || 'N/A',
    },
    {
      key: 'supplier',
      header: 'Supplier',
      render: (item: any) => item.supplier_name || 'N/A',
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (item: any) => {
        const reason = item.reason || 'Failed';
        return (
          <span className="text-sm" title={reason}>
            {reason.length > 40 ? `${reason.slice(0, 40)}...` : reason}
          </span>
        );
      },
    },
    {
      key: 'user_assigned',
      header: 'Assigned To',
      render: (item: any) => item.user_assigned_name || 'Unassigned',
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (item: any) => {
        const status = item.progress_status || 'failed';
        return (
          <span className="text-sm capitalize">{status.replace('_', ' ')}</span>
        );
      },
    },
    {
      key: 'invoice',
      header: 'Invoice',
      render: (item: any) => {
        if (!item.file_url) return 'N/A';
        return (
          <Button variant="ghost" size="sm" asChild>
            <a href={item.file_url} target="_blank" rel="noopener noreferrer">
              <Eye className="h-4 w-4" />
            </a>
          </Button>
        );
      },
    },
    {
      key: 'comments',
      header: 'Comments',
      render: (item: any) => {
        const comments = item.comments || '';
        return (
          <span className="text-sm text-muted-foreground" title={comments}>
            {comments.length > 30 ? `${comments.slice(0, 30)}...` : comments || '-'}
          </span>
        );
      },
    },
  ];

  const getButtonText = () => {
    if (!bulkAction) return 'Select Action';
    if (bulkAction === 'manual') return 'Move to Manual Queue';
    if (bulkAction === 'reparse') return 'Queue for Reparse';
    if (bulkAction === 'issue') return 'Escalate to Issue Queue';
    return 'Apply Action';
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedRows.length === 0) {
      toast({
        title: 'No action selected',
        description: 'Please select an action and items',
        variant: 'destructive',
      });
      return;
    }

    let actionType = '';
    if (bulkAction === 'manual') actionType = 'push_to_manual';
    else if (bulkAction === 'reparse') actionType = 'push_to_reparse';
    else if (bulkAction === 'issue') actionType = 'push_to_issue';

    try {
      await bulkJobAction.mutateAsync({
        jobIds: selectedRows,
        action: actionType as any,
      });
      setSelectedRows([]);
      setBulkAction('');
    } catch (error: any) {
      toast({
        title: 'Bulk action failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <ModuleAccessGuard moduleId="bill_processing">
      <div className="container mx-auto py-6 space-y-6">
        <PageHeader
          title="Dev Parser Queue"
          description="Debug parser issues and reprocess failed bills"
        />

        {selectedRows.length > 0 && (
          <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/50">
            <span className="text-sm font-medium">
              {selectedRows.length} item(s) selected
            </span>
            <Select value={bulkAction} onValueChange={setBulkAction}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select bulk action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Push to Manual Parser</SelectItem>
                <SelectItem value="reparse">Push to Reparse</SelectItem>
                <SelectItem value="issue">Push to Bill Issue</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={handleBulkAction}
              disabled={!bulkAction || bulkJobAction.isPending}
            >
              {bulkJobAction.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                getButtonText()
              )}
            </Button>
            <Button variant="outline" onClick={() => setSelectedRows([])}>
              Clear Selection
            </Button>
          </div>
        )}

        <DataTable
          data={data?.items || []}
          columns={columns}
          totalCount={data?.total || 0}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onRowSelect={setSelectedRows}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          emptyMessage="No failed parser jobs found"
        />
      </div>
    </ModuleAccessGuard>
  );
};

export default DevParserQueue;
